import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from '@tanstack/react-router'
import { Button, Flex, LogoAnimated, Text, Title } from '@nula/core-ui'
import { ApiError } from '@/lib/http/client'
import { useAuthStore } from '@/stores/use-auth-store'
import { acceptInviteLink } from '@/lib/workspace/api/api'
import { useSessionResume } from '@/lib/auth/use-session-resume'
import { useWorkspaceStore } from '@/stores/use-workspace-store'
import {
  PENDING_INVITATION_KEY,
  PENDING_INVITATION_VARIANT,
} from './invitation-accept'
import styles from './join-by-token.module.css'

type JoinState =
  | 'loading'
  | 'accepting'
  | 'forbidden_domain'
  | 'exhausted'
  | 'expired'
  | 'invalid'
  | 'unknown_error'

interface JoinByTokenProps {
  token: string
}

export function JoinByToken({ token }: JoinByTokenProps) {
  // This route sits outside MainLayout so AuthInitializer doesn't run for it.
  // Bootstrap auth here so isLoading transitions to false and the effect below
  // can decide whether to accept the invite (signed in) or stash + redirect to
  // /login (signed out).
  useSessionResume()

  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isLoading = useAuthStore((s) => s.isLoading)
  const refresh = useWorkspaceStore((s) => s.refreshFromServer)
  const [state, setState] = useState<JoinState>('loading')

  useEffect(() => {
    if (isLoading) return
    if (!token) {
      setState('invalid')
      return
    }
    if (!user) {
      // Stash variant + token so NulaApp's post-auth resume calls accept-link
      // (not the entity /invitations/{token}/accept endpoint).
      sessionStorage.setItem(PENDING_INVITATION_KEY, token)
      sessionStorage.setItem(PENDING_INVITATION_VARIANT, 'link')
      void navigate({ to: '/login', replace: true })
      return
    }

    setState('accepting')
    acceptInviteLink(token)
      .then(async ({ workspaceId }) => {
        // Refresh the workspace list so the newly-joined workspace is in the
        // local store, then orchestrate a switch. switchToWorkspace handles:
        //   1. Drain pending ops
        //   2. set activeWorkspaceId → SyncSystemProvider remounts for this
        //      workspace (new WS auth, fresh entity store)
        //   3. Navigate to /<workspaceId>
        await refresh()
        await useWorkspaceStore.getState().switchToWorkspace(
          workspaceId,
          (path) => navigate({ to: path, replace: true }),
        )
      })
      .catch((err: unknown) => {
        console.error('[JoinByToken] acceptInviteLink failed:', err)
        Sentry.captureException(err)
        if (!(err instanceof ApiError)) { setState('unknown_error'); return }
        if (err.status === 403) setState('forbidden_domain')
        else if (err.status === 409) setState('exhausted')
        else if (err.status === 410) setState('expired')
        else if (err.status === 404) setState('invalid')
        else setState('unknown_error')
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isLoading, user])

  if (state === 'loading' || state === 'accepting') {
    return (
      <Flex align="center" justify="center" className={styles.root}>
        <Flex vertical align="center" gap="md">
          <LogoAnimated size={192} loop duration={2000} holdDuration={400} />
          <Text type="secondary">Joining workspace…</Text>
        </Flex>
      </Flex>
    )
  }

  const messages = {
    forbidden_domain: {
      title: "Your email isn't allowed",
      body: "This workspace only allows specific email domains. Ask the workspace admin to add your domain, or sign in with a matching email.",
    },
    exhausted: {
      title: 'Invitation link is used up',
      body: 'This invitation has reached its maximum number of uses. Ask the workspace admin for a new link.',
    },
    expired: {
      title: 'Invitation link has expired',
      body: 'Ask the workspace admin for a new link.',
    },
    invalid: {
      title: 'Invitation link is invalid',
      body: 'Double-check the URL, or ask the workspace admin for a new link.',
    },
    unknown_error: {
      title: 'Something went wrong',
      body: 'Please try again. If the problem persists, contact support.',
    },
  } as const

  const msg = messages[state]

  return (
    <Flex align="center" justify="center" className={styles.root}>
      <Flex vertical align="center" gap="md" className={styles.errorBox}>
        <Title level={2}>{msg.title}</Title>
        <Text type="secondary">{msg.body}</Text>
        <Button onClick={() => void navigate({ to: '/' })}>Go to home</Button>
      </Flex>
    </Flex>
  )
}
